import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import api from '../utils/api';

// ── Ticket icon ───────────────────────────────────────────────────────────────
const TicketIcon = () => (
  <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5"
      d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
  </svg>
);

export default function FindBookingPage() {
  const navigate = useNavigate();

  // Steps: 'lookup' → 'choose' → 'send' → 'done'
  const [step, setStep] = useState('lookup');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [sent, setSent] = useState(null); // { masked, delivery }

  // Pagination for booking list
  const PAGE_SIZE = 8;
  const [page, setPage] = useState(0);
  const handleLookup = async (e) => {
    e.preventDefault();
    setError('');
    if (!lastName.trim()) { setError('Please enter your last name'); return; }
    if (phone.replace(/\D/g, '').length < 7) { setError('Please enter a valid phone number'); return; }

    setLoading(true);
    try {
      const res = await api.post('/v2/public/find-booking', {
        lastName: lastName.trim(),
        phone: phone.trim(),
      });
      const found = res.data.bookings || [];
      setBookings(found);
      if (found.length === 1) {
        setSelectedBooking(found[0]);
        setStep('send');
      } else {
        setStep('choose');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'No booking found with those details. Please check your last name and phone number.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 3: Send ticket ──────────────────────────────────────────────────
  const handleSend = async (delivery) => {
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/v2/public/find-booking/send', {
        confirmationCode: selectedBooking.confirmationCode,
        delivery,
      });
      setSent({ masked: res.data.masked, delivery });
      setStep('done');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to send. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-gray-50 flex flex-col items-center justify-center p-4">
      {/* Back link — minimal, no header bar */}
      <div className="w-full max-w-md mb-6">
        <Link to="/" className="text-sm text-gray-400 hover:text-gray-600 transition-colors flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </Link>
      </div>

      <Card className="max-w-md w-full shadow-xl border-0">
        <CardContent className="p-8">

          {/* ── STEP 1: Lookup ── */}
          {step === 'lookup' && (
            <>
              <div className="text-center mb-7">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-5">
                  <TicketIcon />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Find My Booking</h1>
                <p className="text-gray-500 text-sm">
                  Enter your last name and the phone number you used when booking.
                  We'll send your ticket to your registered contact.
                </p>
              </div>

              <form onSubmit={handleLookup} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                  <Input
                    placeholder="Your last name"
                    value={lastName}
                    onChange={e => setLastName(e.target.value.replace(/[^a-zA-Z\s'\-.]/g, '').slice(0, 50))}
                    maxLength={50}
                    className="h-12 text-base border-gray-200 focus:border-blue-500"
                    autoFocus
                    autoComplete="family-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                  <Input
                    type="tel"
                    placeholder="Your phone number"
                    value={phone}
                    onChange={e => setPhone(e.target.value.replace(/[^0-9+()\-\s]/g, '').slice(0, 20))}
                    maxLength={20}
                    className="h-12 text-base border-gray-200 focus:border-blue-500"
                    autoComplete="tel"
                  />
                  <p className="text-xs text-gray-400 mt-1">Any format · no country code needed for US numbers</p>
                </div>

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                <Button type="submit"
                  disabled={loading || !lastName.trim() || phone.replace(/\D/g, '').length < 7}
                  className="w-full h-12 text-base font-semibold bg-blue-600 hover:bg-blue-700 text-white">
                  {loading ? 'Searching...' : 'Find My Booking'}
                </Button>
                <p className="text-xs text-gray-400 text-center">5 attempts max · your details are never stored</p>
              </form>
            </>
          )}

          {/* ── STEP 2: Choose booking (multiple found) ── */}
          {step === 'choose' && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-1">We found {bookings.length} bookings</h2>
                <p className="text-gray-500 text-sm">Select the booking you'd like to access</p>
              </div>

              <div className="space-y-3">
                {bookings.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((b) => (
                  <button
                    key={b.confirmationCode}
                    onClick={() => { setSelectedBooking(b); setStep('send'); }}
                    className="w-full text-left p-4 rounded-xl border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{b.serviceName}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{b.date}{b.orgName ? ` · ${b.orgName}` : ''}</p>
                      </div>
                      <span className="text-xs font-mono text-gray-400">{b.confirmationCode}</span>
                    </div>
                  </button>
                ))}
              </div>

              {/* Pagination */}
              {bookings.length > PAGE_SIZE && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="text-sm text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/></svg>
                    Previous
                  </button>
                  <span className="text-xs text-gray-400">
                    {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, bookings.length)} of {bookings.length}
                  </span>
                  <button
                    onClick={() => setPage(p => p + 1)}
                    disabled={(page + 1) * PAGE_SIZE >= bookings.length}
                    className="text-sm text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    Next
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/></svg>
                  </button>
                </div>
              )}

              <button onClick={() => { setStep('lookup'); setError(''); setPage(0); }}
                className="w-full text-center text-sm text-gray-400 hover:text-gray-600 mt-4">
                ← Search again
              </button>
            </>
          )}

          {/* ── STEP 3: Choose delivery method ── */}
          {step === 'send' && selectedBooking && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-1">Booking found!</h2>
                <div className="inline-flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-1.5 mt-1">
                  <span className="text-sm text-gray-600">{selectedBooking.serviceName}</span>
                  <span className="text-gray-300">·</span>
                  <span className="text-xs font-mono text-gray-500">{selectedBooking.confirmationCode}</span>
                </div>
              </div>

              <p className="text-sm text-gray-600 text-center mb-5 font-medium">How should we send your ticket?</p>

              <div className="space-y-3">
                {selectedBooking.hasEmail && (
                  <button
                    onClick={() => handleSend('email')}
                    disabled={loading}
                    className="w-full flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all disabled:opacity-50"
                  >
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-semibold text-gray-900">Email my ticket</p>
                      <p className="text-xs text-gray-500">Send to {selectedBooking.maskedEmail}</p>
                    </div>
                  </button>
                )}

                {selectedBooking.hasPhone && (
                  <button
                    onClick={() => handleSend('sms')}
                    disabled={loading}
                    className="w-full flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all disabled:opacity-50"
                  >
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-semibold text-gray-900">Text my ticket</p>
                      <p className="text-xs text-gray-500">Send to number {selectedBooking.maskedPhone}</p>
                    </div>
                  </button>
                )}
              </div>

              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {loading && <p className="text-center text-sm text-gray-400 mt-4">Sending...</p>}

              {bookings.length > 1 && (
                <button onClick={() => setStep('choose')}
                  className="w-full text-center text-sm text-gray-400 hover:text-gray-600 mt-4">
                  ← Choose a different booking
                </button>
              )}
            </>
          )}

          {/* ── STEP 4: Sent ── */}
          {step === 'done' && sent && (
            <div className="text-center py-4">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {sent.delivery === 'email' ? 'Email sent!' : 'Text sent!'}
              </h2>
              <p className="text-gray-500 text-sm mb-1">
                Your ticket link was {sent.delivery === 'email' ? 'emailed' : 'texted'} to
              </p>
              <p className="font-mono text-sm font-semibold text-gray-800 mb-6">{sent.masked}</p>
              <p className="text-xs text-gray-400">
                Check your {sent.delivery === 'email' ? 'inbox (and spam folder)' : 'messages'}.
                The link is secure and expires in 30 days.
              </p>
              <button onClick={() => { setStep('lookup'); setLastName(''); setPhone(''); setError(''); setSent(null); }}
                className="mt-6 text-sm text-blue-600 hover:underline">
                Look up another booking
              </button>
            </div>
          )}

        </CardContent>
      </Card>
    </div>
  );
}
